import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card } from "antd";
import { GeometryPicker } from "@/modules/plantings/components/GeometryPicker";
import type { PlantingGeometry } from "@/modules/plantings/types";
import type { AreaGeometry, PointGeometry, TaskLocationType } from "../types";

/** In-progress list entry — geometry is filled in via the map, so it starts undefined. */
export interface TaskLocationDraft {
	type: TaskLocationType;
	geometry?: PointGeometry | AreaGeometry;
}

interface TaskLocationListInputProps {
	value?: TaskLocationDraft[];
	onChange?: (value: TaskLocationDraft[]) => void;
}

/** Repeatable list of task locations, each drawn on its own map via GeometryPicker. */
export function TaskLocationListInput({ value = [], onChange }: TaskLocationListInputProps) {
	const setEntryGeometry = (index: number, geometry: PlantingGeometry) => {
		onChange?.(
			value.map((entry, i) =>
				i === index ? { ...entry, geometry: geometry as PointGeometry | AreaGeometry } : entry
			)
		);
	};

	const setEntryType = (index: number, type: TaskLocationType) => {
		onChange?.(value.map((entry, i) => (i === index ? { type, geometry: undefined } : entry)));
	};

	const addEntry = () => {
		onChange?.([...value, { type: "POINT", geometry: undefined }]);
	};

	const removeEntry = (index: number) => {
		onChange?.(value.filter((_, i) => i !== index));
	};

	return (
		<div className="space-y-3">
			{value.map((entry, index) => (
				<Card
					// biome-ignore lint/suspicious/noArrayIndexKey: entries have no stable id before submit
					key={index}
					size="small"
					title={`Joylashuv ${index + 1}`}
					extra={
						<Button
							type="text"
							danger
							icon={<MinusCircleOutlined />}
							onClick={() => removeEntry(index)}
						/>
					}
				>
					<GeometryPicker
						mode={entry.type === "AREA" ? "polygon" : "point"}
						onModeChange={(mode) => setEntryType(index, mode === "polygon" ? "AREA" : "POINT")}
						value={entry.geometry as PlantingGeometry | undefined}
						onChange={(geometry) => setEntryGeometry(index, geometry)}
					/>
				</Card>
			))}
			<Button type="dashed" block icon={<PlusOutlined />} onClick={addEntry}>
				Joylashuv qo'shish
			</Button>
		</div>
	);
}
